import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Flex } from 'styling/GlobalStyles'
import {
  BedContainer,
  BedFooter,
  BedMiddleSection,
  BlockStatusesContainer,
  CampContainer,
  CampOverviewContainer,
  ColoredSquare,
} from '../Accommodation.style'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import Chevron from 'assets/icons/Chevron'
import { ThreeDots } from 'assets/icons/ThreeDots'
import { PersonIcon } from '../icons/PersonIcon'
import { AppDispatch, RootState } from 'store'
import { clearBeds, getBeds } from 'features/accommodation/accommodationSlice'
import { formatParams } from 'utils/helpers'
import { IGetAllAvailableBedsSingleResponse } from 'types/api/accommodationApi.interface'

const colors = {
  totalCapacity: '#ff9800',
  available: '#10B981',
  occupied: '#51A2FF',
  dirty: '#F4D03F',
  blocked: '#FF6467',
}

const BED_STATE_MAP: Record<number, { label: string; color: string }> = {
  1: { label: 'AVAILABLE', color: colors.available },
  2: { label: 'OCCUPIED', color: colors.occupied },
  3: { label: 'DIRTY', color: colors.dirty },
  4: { label: 'BLOCKED', color: colors.blocked },
}

const getBedStateInfo = (bedState: number) =>
  BED_STATE_MAP[bedState] ?? { label: 'UNKNOWN', color: colors.blocked }

const groupBedsByCamp = (
  beds: IGetAllAvailableBedsSingleResponse[],
): Record<string, IGetAllAvailableBedsSingleResponse[]> => {
  return beds.reduce(
    (acc, bed) => {
      const campName = bed.campName || 'Unassigned'
      if (!acc[campName]) acc[campName] = []
      acc[campName].push(bed)
      return acc
    },
    {} as Record<string, IGetAllAvailableBedsSingleResponse[]>,
  )
}

export const BedManagement = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { beds, isLoading } = useSelector((store: RootState) => store.accomodation)
  const [params, setParams] = useState({ page: 1, limit: 20 })
  const [selectedCamp, setSelectedCamp] = useState<{ value: number; label: string } | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const isInitialMount = useRef(true)

  const bedsGroupedByCamp = useMemo(
    () => groupBedsByCamp(beds.data),
    [beds.data],
  )

  const hasMore = params.page < beds.totalPages
  const campEntries = Object.entries(bedsGroupedByCamp)

  useEffect(() => {
    const fetchParams = {
      ...params,
      ...(selectedCamp?.value && { campId: selectedCamp.value }),
    }
    dispatch(getBeds(formatParams(fetchParams)))
  }, [dispatch, params.page, params.limit, selectedCamp?.value])

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    dispatch(clearBeds())
    setParams((p) => ({ ...p, page: 1 }))
  }, [selectedCamp?.value, dispatch])

  const lastPostElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading || !hasMore) return
      if (observerRef.current) observerRef.current.disconnect()

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          setParams((prev) => ({ ...prev, page: prev.page + 1 }))
        }
      })

      if (node) observerRef.current.observe(node)
    },
    [isLoading, hasMore],
  )

  const BlockSection = ({
    campName,
    campBeds,
  }: {
    campName: string
    campBeds: IGetAllAvailableBedsSingleResponse[]
  }) => {
    const [active, setActive] = useState(false)
    const roomsCount = new Set(campBeds.map((b) => b.roomName)).size
    const bedsCount = campBeds.length

    return (
      <CampContainer active={active} onClick={() => setActive(!active)}>
        <CampOverviewContainer>
          <Flex column between height="100%">
            <BodyText style={{ fontSize: 20 }}>{campName}</BodyText>
            <BodyText grey>
              {roomsCount} rooms · {bedsCount} beds total
            </BodyText>
          </Flex>
          <Flex height="100%" gap="1rem" center>
            <Chevron open={active} />
          </Flex>
        </CampOverviewContainer>
        <BlockStatusesContainer style={{ padding: '1rem' }} fr="3">
          {campBeds.map((bed) => (
            <BedItem key={bed.id} bed={bed} />
          ))}
        </BlockStatusesContainer>
      </CampContainer>
    )
  }

  const BedItem = ({ bed }: { bed: IGetAllAvailableBedsSingleResponse }) => {

    const stateInfo = getBedStateInfo(bed.bedState)
    return (
      <BedContainer onClick={(e) => e.stopPropagation()}>
        <Flex gap="0.5rem" width="100%" between>
          <Flex gap="0.5rem">
            <ColoredSquare style={{ height: 40, width: 40 }} color={stateInfo.color} />
            <Flex column between>
              <TitleText>{bed.label}</TitleText>
              <BodyText color={stateInfo.color}>{stateInfo.label}</BodyText>
            </Flex>
          </Flex>
          {/* <ThreeDots /> */}
        </Flex>

        <BedMiddleSection borderColor={stateInfo.color}>
          <Flex gap="0.5rem">
            <PersonIcon />
            <OtherText color={stateInfo.color}>ROOM: {bed.roomName}</OtherText>
          </Flex>
          <BodyText style={{ margin: '0.5rem 0' }}>{bed.blockName}</BodyText>
        </BedMiddleSection>

        <BedFooter>
          <OtherText>BED POLICIES</OtherText>
          <Flex width="100%" between>
            <OtherText grey>Gender:</OtherText>
            <BodyText weight="bold">
              {bed.blockGenderPolicy === 1 ? 'MALE' : bed.blockGenderPolicy === 2 ? 'FEMALE' : 'ANY'}
            </BodyText>
          </Flex>
          <Flex width="100%" between>
            <OtherText grey>Tier:</OtherText>
            <BodyText weight="bold">
              {bed.blockTierPolicy === 1
                ? 'STANDARD'
                : bed.blockTierPolicy === 2
                  ? 'PREMIUM'
                  : 'ANY'}
            </BodyText>
          </Flex>
        </BedFooter>
      </BedContainer>
    )
  }

  return (
    <>
      {/* <Box
        width="100%"
        height="105px"
        style={{
          padding: '1rem',
          borderTop: '1px solid #ff9900',
          borderRadius: '0.5rem',
        }}
      >
         <HonestSelectInput
          name="camp"
          labelText="Select Camp"
          options={[]}
          onChange={(v) =>
            setSelectedCamp(
              Array.isArray(v) ? null : (v as { value: number; label: string } | null),
            )
          }
          width="335px"
        /> 
      </Box> */}
      <Flex column gap="1rem" mt="16">
        {campEntries.map(([campName, campBeds]) => (
          <BlockSection key={campName} campName={campName} campBeds={campBeds} />
        ))}
        {hasMore && (
          <div ref={lastPostElementRef} style={{ height: 20, width: '100%' }}>
            {isLoading && <BodyText grey>Loading more...</BodyText>}
          </div>
        )}
      </Flex>
    </>
  )
}
